import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { GoogleLogo } from "@phosphor-icons/react";

export default function Register() {
  const { register } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await register(form);
      toast.success("Account created");
      nav("/account");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const startGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="page-enter mx-auto max-w-md px-6 py-24">
      <div className="overline mb-3">Account</div>
      <h1 className="font-display text-5xl leading-none">Create account.</h1>

      <button
        type="button"
        onClick={startGoogle}
        className="mt-8 w-full inline-flex items-center justify-center gap-3 px-5 py-3.5 rounded-full bg-white text-black font-semibold text-sm hover:bg-zinc-200 transition-colors"
        data-testid="register-google"
      >
        <GoogleLogo size={18} weight="bold" />
        Continue with Google
      </button>

      <div className="flex items-center gap-3 my-6">
        <div className="flex-1 h-px bg-white/10" />
        <div className="overline">or email</div>
        <div className="flex-1 h-px bg-white/10" />
      </div>

      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        <Field label="Full name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="reg-name" />
        <Field label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="reg-email" />
        <Field label="Password (min. 8)" type="password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} testid="reg-password" />
        {error && <div className="text-sm text-red-500" data-testid="register-error">{error}</div>}
        <button className="btn-prm w-full" disabled={loading} data-testid="register-submit">
          {loading ? "Creating…" : "Create account"}
        </button>
      </form>
      <div className="mt-6 text-sm text-zinc-500">
        Already have one?{" "}
        <Link to="/login" className="link-underline" data-testid="register-to-login">Sign in</Link>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <label className="block">
      <div className="overline mb-1">{label}</div>
      <input
        type={type}
        value={value}
        required
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-white/15 px-3 py-3 outline-none focus:border-blue-500 bg-white/[0.03] text-sm rounded-lg"
        data-testid={testid}
      />
    </label>
  );
}
